import { DateTime } from 'luxon';

// Used for select dropdown grouping
export const formatGroupLabel = (data) => {
	const groupStyles = {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	};

	const groupBadgeStyles = {
		backgroundColor: '#EBECF0',
		borderRadius: '2em',
		color: '#179b77',
		display: 'inline-block',
		fontSize: 12,
		fontWeight: 'normal',
		lineHeight: '1',
		minWidth: 1,
		padding: '0.16666666666667em 0.5em',
		textAlign: 'center',
	};

	return (
		<div style={groupStyles}>
			<span>{data.label}</span>
			<span style={groupBadgeStyles}>{data.options.length}</span>
		</div>
	);
};

export const groupBy = (array, key) =>
	array.reduce((rv, x) => {
		(rv[x[key]] = rv[x[key]] || []).push(x);
		return rv;
	}, {});

export function arrayUniqueByKey(array, key) {
	return [...new Map(array.map((item) => [item[key], item])).values()];
}

export function getGlobalDateFormat() {
	return 'dd LLL yyyy HH:mm';
}

export function nowAsTs() {
	return DateTime.local().toSeconds();
}

export function tomorrowAsTs() {
	return DateTime.local().plus({ days: 1 }).toSeconds();
}

export function timeAgo(ts) {
	// return format(DateTime.local(), 'en-GB', { relativeDate: ts });

	const _d = DateTime.fromSeconds(Number(ts))
		.setLocale('en-GB')
		.diffNow('hours');

	return DateTime.local()
		.plus({ hours: _d.hours })
		.toRelative({ unit: 'hours' });
}

export function tsToJSDate(ts) {
	const tsParsed = parseInt(ts);
	if (isNaN(tsParsed) || tsParsed == 0) return '-';

	const _d = DateTime.fromSeconds(tsParsed).setLocale('en-GB');

	if (_d.invalid) return '-';

	return _d.toJSDate();
}

export function timeStampToDateTime(ts) {
	const tsParsed = parseInt(ts);
	if (isNaN(tsParsed) || tsParsed == 0) return '-';

	const _d = DateTime.fromSeconds(tsParsed).setLocale('en-GB');

	if (_d.invalid) return '-';

	return _d.toFormat(getGlobalDateFormat());
}

export function timeStampToDateTimeGmt(ts) {
	const tsParsed = parseInt(ts);
	if (isNaN(tsParsed) || tsParsed == 0) return '-';

	const _d = DateTime.fromSeconds(tsParsed).setLocale('en-GB').toUTC();

	if (_d.invalid) return '-';

	return _d.toFormat(getGlobalDateFormat());
}

export function timeStampToDate(ts) {
	const tsParsed = parseInt(ts);
	if (isNaN(tsParsed) || tsParsed == 0) return '-';

	const _d = DateTime.fromSeconds(tsParsed).setLocale('en-GB');

	if (_d.invalid) return '-';

	return _d.toFormat('dd LLL yyyy');
}

export function jsDateToDate(date) {
	const _d = DateTime.fromJSDate(date).setLocale('en-GB');

	if (_d.invalid) return '-';

	return _d.toFormat('dd LLL yyyy');
}

export function dateToJSDate(date) {
	const _d = DateTime.fromISO(date).setLocale('en-GB');

	if (_d.invalid) return '-';

	return _d.toJSDate();
}

export function jsDateToSortableDate(date) {
	const _d = DateTime.fromJSDate(date).setLocale('en-GB');

	if (_d.invalid) return '-';

	return _d.toFormat('yyyy-LL-dd');
}

export function jsDateToTS(date) {
	const _d = DateTime.fromJSDate(date).setLocale('en-GB');

	if (_d.invalid) return null;

	return _d.toSeconds();
}

export const escapeRegExp = (string) => {
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export const decodeHTML = function (html) {
	var txt = document.createElement('textarea');
	txt.innerHTML = html;
	return txt.value;
};

export const handleServerError = (res) => {
	if (!res.ok) {
		console.log('inside');
		throw new Error(res?.err ?? '');
	}

	return res;
};

export const fetchWithErr = (url, params) => {
	const opts = {
		...params,
		headers: {
			'Content-Type': 'application/json',
		},
	};

	return fetch(url, opts)
		.then((response) => {
			if (!response.ok) {
				throw response;
			}
			return response.json(); //we only get here if there is no error
		})
		.catch(async (err) => {
			const text = await err.text();

			const json = JSON.parse(text);
			if (json) throw json;

			throw {
				msg: text,
			};
		});
};

export const getLangNameFromCode = (code, langs) => {
	const _l = langs.find((l) => l.id == code);
	return _l ? _l.name : code;
};

export function downloadTranslationDirect(url, fileName) {
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', fileName);
	link.style.display = 'none';
	document.body.appendChild(link);
	link.click();
	link.remove();
}

export async function downloadTranslationAsync(url, fileName) {
	const downloadResp = await fetch(url);
	if (!downloadResp.ok) {
		const text = await downloadResp.text();
		throw {
			msg: text || `Download failed with status ${downloadResp.status}`,
			status: downloadResp.status,
		};
	}

	const downloadRespData = await downloadResp.blob();

	const downloadUrl = window.URL.createObjectURL(new Blob([downloadRespData]));
	const link = document.createElement('a');
	link.href = downloadUrl;
	link.setAttribute('download', fileName);
	document.body.appendChild(link);
	link.click();
	link.remove();
}

// Used to format created/updated fields of TM/TB entries
export const formatNameAndTS = (name, ts, step = null, isMT = false) => {
	let text = 'N/A';

	if (ts && ts != 'N/A') text = timeStampToDateTime(ts);

	if (!isMT) {
		if (name && name != 'N/A') {
			text += ' by ' + name;
		} else {
			text += ' by N/A';
		}
	}

	if (step && step != 'N/A') text += ` (${step})`;

	return text;
};

export const setEngineProgress = (engineAbbr) =>
	engineAbbr?.toLowerCase().includes('dpl') ||
	engineAbbr?.toLowerCase().includes('gt') ||
	engineAbbr?.toLowerCase() === 'sa2'
		? 100
		: 0;

export const shouldDisableSegmentsByTaskStatus = (status) => {
	if (
		status?.toLowerCase() == 'new' ||
		status?.toLowerCase() == 'declined' ||
		status?.toLowerCase() == 'emailed' ||
		status?.toLowerCase() == 'auto-emailed'
	) {
		return true;
	}

	return false;
};

export const wordsCount = (
	str,
	commonRegex = `[!"\#$%&'()*+,\-./:;<=>?@\[\\\]^_–‘{|}~\–]`
) =>
	str
		.replace(new RegExp(`/\s+${commonRegex}+\s+/g`), ' ')
		.replace(new RegExp(`/^${commonRegex}+\s+/g`), ' ')
		.replace(new RegExp(`/\s+${commonRegex}+$/g`), ' ')
		.replace(/\s\s+/g, ' ')
		.trim()
		.split(' ')
		.filter((s) => s != '').length ?? 0;

export const nextTick = (fn) => {
	Promise.resolve().then(fn);
};

export const firstToUpper = (s) => (s && s[0].toUpperCase() + s.slice(1)) || '';
export const firstToLower = (s) => (s && s[0].toLowerCase() + s.slice(1)) || '';

// Used when displaying backend DateTimes that are already "localed"
export function timeStampToDateTimeWithZone(ts) {
	const tsParsed = parseInt(ts);
	if (isNaN(tsParsed) || tsParsed == 0) return '-';

	const _d = DateTime.fromSeconds(tsParsed, {
		zone: 'Europe/London',
	});

	if (_d.invalid) return '-';

	return _d.toFormat(getGlobalDateFormat());
}

export const getUserInitials = (user) => {
	if (user?.name && user?.lastName) {
		return `${user.name[0]}${user.lastName[0]}`;
	}

	let initials = user?.name?.toUpperCase();

	const splitName = user?.name?.split(' ');

	if (splitName?.[1]) {
		initials = `${splitName[0][0]?.toUpperCase()}${splitName[1][0]?.toUpperCase()}`;
	}

	return initials;
};
